/**
 * Where a signed-in user lands when the address names no screen.
 * ---------------------------------------------------------------------------
 * A bare `/` (the address after sign-in, or typed) sends approvers to the
 * Approval Desk and everyone else to Home. Holding approvals:desk decides it at
 * once; without the role the server is asked (useLandingProfile, which grants
 * the role to an approver), so that answer is not known at first render.
 * A saved Settings choice of Home still wins.
 *
 * While it is pending, `landingPending` is true and App holds MainContent back,
 * so Home does not flash up and then bounce to the desk. The rules:
 *
 *   - Only a URL that names no view ever holds. `?view=`, a deep-link path and
 *     a legacy `?tab=approvals` link all say where to go, and they win.
 *     Read once, at first render, before sign-in strips `?samlToken=`.
 *   - The hold is capped at LANDING_PROFILE_TIMEOUT_MS. A slow or failed
 *     profile request settles on this person's LAST answer (remembered per
 *     user in localStorage), or Home if there is none -- so an approver is
 *     not dropped on Home just because the server was slow this once. The
 *     memory only picks a landing page; it grants nothing.
 *   - It settles ONCE. An answer that arrives after the timeout is ignored:
 *     moving somebody off Home a second later, after they have started
 *     reading it, is worse than landing them in the wrong place.
 *   - An explicit navigation during the hold (the sidebar stays usable) ends
 *     it via `endLandingHold`, and the URL is re-checked when it settles, so a
 *     choice the user already made is never overridden.
 *   - The redirect uses replaceState before MainContent mounts, so Back does
 *     not return to a bare `/` that would redirect again.
 */

import { useCallback, useEffect, useState } from 'react';
import { resolveDefaultView, urlForView, urlNamesNoView } from '../utils/navigation';
import { useLandingProfile } from './useLandingProfile';
import { useMyProfile } from './useMyProfile';

/** How long a view-less URL waits for the landing profile before settling without it. */
export const LANDING_PROFILE_TIMEOUT_MS = 1500;

const rememberedKey = (who: string) => `gra.landing.isApprover.${who}`;

function recallIsApprover(who: string | undefined): boolean {
  if (!who) return false;
  try {
    return window.localStorage.getItem(rememberedKey(who)) === 'true';
  } catch {
    return false;
  }
}

function rememberIsApprover(who: string | undefined, isApprover: boolean): void {
  if (!who) return;
  try {
    window.localStorage.setItem(rememberedKey(who), String(isApprover));
  } catch {
    // Storage unavailable: a slow profile falls back to Home, as before.
  }
}

// The saved landing choice (Settings), remembered on this device too. The
// profile that carries it can arrive after the wait cap -- most often just
// after sign-in, while everything loads at once -- and settling without it
// sent people to the default instead of the page they chose.
const choiceKey = (who: string) => `gra.landing.choice.${who}`;

function recallChoice(who: string | undefined): 'home' | 'approvals' | null {
  if (!who) return null;
  try {
    const v = window.localStorage.getItem(choiceKey(who));
    return v === 'home' || v === 'approvals' ? v : null;
  } catch {
    return null;
  }
}

function rememberChoice(who: string | undefined, choice: 'home' | 'approvals' | null): void {
  if (!who) return;
  try {
    if (choice) window.localStorage.setItem(choiceKey(who), choice);
    else window.localStorage.removeItem(choiceKey(who));
  } catch {
    // Storage unavailable: a slow profile falls back to the default, as before.
  }
}

export interface DefaultLanding {
  /** True while a view-less URL is waiting to learn where it lands. */
  landingPending: boolean;
  /** Ends the hold without redirecting. Call on any explicit navigation. */
  endLandingHold: () => void;
}

export function useDefaultLanding(
  token: string | null | undefined,
  setActiveItem: (view: string) => void,
  /** Stable per-person key (user id or email) for the remembered answer. */
  who?: string,
  /** The signed-in roles already open the Approval Desk (approvals:desk or platform admin). */
  holdsDeskRole = false
): DefaultLanding {
  const [landingPending, setLandingPending] = useState<boolean>(() => urlNamesNoView());
  const landing = useLandingProfile(token);
  // The saved landing choice. Waited for alongside the approver answer, under
  // the same cap; past the cap, the choice last seen on this device stands in.
  const me = useMyProfile(token);
  const preferred =
    me.loading || me.profile.failed ? recallChoice(who) : me.profile.preferences.landingPage;

  useEffect(() => {
    if (!landingPending || !token) return undefined;
    const settle = (isApprover: boolean): void => {
      setLandingPending(false);
      // The sidebar stays usable during the wait; a click there already chose.
      if (!urlNamesNoView()) return;
      const view = resolveDefaultView(isApprover, preferred);
      if (view === 'Home') return;
      window.history.replaceState({}, '', urlForView(view));
      setActiveItem(view);
    };
    if ((holdsDeskRole || !landing.loading) && !me.loading) {
      // A failed request, or a server that could not read every signal, is
      // not an answer: settle on the remembered one instead.
      const unsure = landing.failed || landing.incomplete;
      settle(holdsDeskRole || (unsure ? recallIsApprover(who) : landing.isApprover));
      return undefined;
    }
    const timer = setTimeout(
      () => settle(holdsDeskRole || recallIsApprover(who)),
      LANDING_PROFILE_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [
    landingPending,
    token,
    holdsDeskRole,
    landing.loading,
    landing.isApprover,
    landing.failed,
    landing.incomplete,
    me.loading,
    preferred,
    setActiveItem,
    who,
  ]);

  // Keep the device's copy of the saved choice current (including after a save
  // in Settings, which republishes the profile).
  useEffect(() => {
    if (token && !me.loading && !me.profile.failed) rememberChoice(who, me.profile.preferences.landingPage);
  }, [token, who, me.loading, me.profile.failed, me.profile.preferences.landingPage]);

  // Remember every real answer, whether or not it arrived in time to be used.
  // A failed request is not an answer, so it never overwrites one.
  useEffect(() => {
    if (token && !landing.loading && !landing.failed && !landing.incomplete) {
      rememberIsApprover(who, landing.isApprover);
    }
  }, [token, who, landing.loading, landing.failed, landing.incomplete, landing.isApprover]);

  const endLandingHold = useCallback(() => setLandingPending(false), []);

  return { landingPending, endLandingHold };
}
